<template>
  <div class="text-gray-600">
    <div class="bg-gray-50 text-center py-10">
      <h1 class="ota-title mb-1" v-text="$t('Lookbook.Lookbook')" />
      <h2 class="ota-small-light" v-text="$t('Lookbook.SeeAllDesigns')" />
    </div>
    <MembershipSlider
      class="my-15"
      variant="lookbook"
      :orientation-id="orientationId"
      show-socks
      @explore="redirectToLookbook($event)"
    />
    <div class="text-center mb-7 md:mb-12">
      <h3 class="ota-title mb-1" v-text="membershipData[orientationId].title" />
      <h4 class="ota-small-light" v-text="membershipData[orientationId].body" />
    </div>
    <div class="no-padding-container container">
      <Accordion
        v-for="year in years"
        :key="year"
        :year="year"
        :open="year === today.format('YYYY')"
        class="mb-3 md:mb-7"
        @click="fetchDesigns"
      >
        <div v-if="designs[year]" class="container">
          <transition name="fade" appear>
            <div class="row">
              <DesignThumbnail v-for="(design) in designs[year]" :key="design.id" class="mb-5 col-md-6 col-lg-4" v-bind="getDesignThumbnailProps(design)" @click="redirectToSingleBoxerPage" />
            </div>
          </transition>
        </div>
        <div v-else v-loading="true" class="flex justify-center h-10" />
      </Accordion>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import {
  GroupIdsByName,
  OrientationsStartDates, Orientations, OrientationSlugsByGroupIds
} from '@/enums/products'
import { GET_DESIGNS } from '@/utils/store/actionTypes'
import { getImagePaths } from '@/utils/general'
import Accordion from '@/components/Accordion'
const MembershipSlider = () => import('@/components/MembershipSlider')
const DesignThumbnail = () => import('@/components/DesignThumbnail')

export default {
  name: 'LookBook',
  layout: 'homepage',
  middleware: 'landing',
  components: {
    MembershipSlider,
    DesignThumbnail,
    Accordion
    // OSvg
  },
  async fetch () {
    await this.fetchDesigns({ year: this.today.format('YYYY'), active: true })
  },
  data () {
    return {
      designs: {}
    }
  },
  computed: {
    orientationId () {
      return GroupIdsByName[this.$route.params.orientation]
    },
    isMenOne () {
      return this.orientationId === GroupIdsByName[Orientations.menone]
    },
    membershipData () {
      const membershipData = [...Array(3)].reduce((acc, _, index) => {
        const orientationId = this.$t(`HomePage.MembershipTypes[${index}].value`)

        return {
          ...acc,
          [orientationId]: {
            title: this.$t(`HomePage.MembershipTypes[${index}].title`),
            body: this.$t(`HomePage.MembershipTypes[${index}].body`)
          }
        }
      }, {})

      return { // TODO: remove this and add socks to MembershipTypes translations like above, when socks are shows across the whole website
        ...membershipData,
        [GroupIdsByName[Orientations.socksoriginals]]: {
          title: this.$t('Socks.Socks'),
          body: this.$t('HomePage.MembershipTypes[1].body')
        }
      }
    },
    today () {
      return this.$moment()
    },
    years () {
      const startData = this.$moment(OrientationsStartDates[this.orientationId], 'DD-MM-YYYY')

      const interim = startData.clone()
      const timeValues = []

      // eslint-disable-next-line
      while (this.today > interim || interim.format('YYYY') === this.today.format('YYYY')) {
        timeValues.push(interim.format('YYYY'))
        interim.add(1, 'year')
      }

      return timeValues.reverse()
    }
  },
  methods: {
    redirectToLookbook (id) {
      this.$router.push(this.$i18n.localePath({ name: 'lookbook-orientation', params: { orientation: OrientationSlugsByGroupIds[id] } }))
    },
    redirectToSingleBoxerPage (slug = '') {
      this.$router.push(this.$i18n.localePath({ name: 'lookbook-orientation-slug', params: { orientation: OrientationSlugsByGroupIds[this.orientationId], slug: slug.toLowerCase() } }))
    },
    async fetchDesigns ({ year, active }) {
      if (active && !this.designs[year]) {
        try {
          const payload = {
            product_groups: [this.$route.params.orientation],
            year,
            sort: 'release_date',
            direction: 'desc'
          }

          const designs = await this.getDesigns(payload)

          this.designs = {
            ...this.designs,
            [year]: designs
          }
        } catch (error) {
          this.$ota.general.logError(error)
          this.$otoaster.danger(this.$t('Error.general'))
        }
      }
    },
    getDesignThumbnailProps ({ name, releaseDate, primaryColor, slug }) {
      return {
        name,
        date: this.$moment(releaseDate).format('M'),
        image: this.createLookbookDesignImage({ slug, orientation: this.$route.params.orientation }),
        color: primaryColor,
        slug
      }
    },
    createLookbookDesignImage ({ slug, orientation }) {
      if (orientation === Orientations.menone) {
        return getImagePaths({
          path: `/images/design/${slug}/campaign/alt-${slug}-${orientation}`,
          s3: true
        })
      }

      return getImagePaths({
        path: `/images/design/${slug}/campaign/${slug}-${orientation}`,
        s3: true
      })
    },
    ...mapActions('lookbook', {
      getDesigns: GET_DESIGNS
    })
  },
  head () {
    return {
      title: 'Lookbook' // TODO: translate
    }
  }
}
</script>

<style lang="postcss" scoped>
.membership-slider-lookbook >>> .oimage img {
  max-width: 11.8rem;
}

>>> .accordion-active {
  @apply mb-10;
}

.no-padding-container {
  @apply px-0 !important;
}

>>> .design-thumbnail {
  min-height: 25.2rem;
}

.fade-enter-active {
  @apply transition-opacity ease-in duration-1000 delay-200;
}

.fade-enter {
  @apply opacity-0;
}

.design-thumbnail {
  &:last-child {
    @apply mb-0;
  }
}

@screen md {
  >>> .accordion-active {
    @apply mb-12;
  }

  .design-thumbnail {
    min-height: 25rem;

    &:last-child {
      @apply mb-5;
    }
  }
}
</style>
